import clsx from 'clsx';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MenuItems } from 'src/app/AppRoutes';
import { ProLogo } from 'src/components/common/ProLogo';
import { CustomTabIcon } from 'src/components/CustomTabs/customTabIcons';
import { useCustomTabsContext } from 'src/components/CustomTabs/CustomTabsContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useOnClickOutside } from 'src/hooks/useClickOutside';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { CrossIcon, IconsFill } from 'src/svgs/Viagogo';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';
import { hasAccessToRoute } from 'src/utils/userUtils';
import { Feature } from 'src/WebApiController';

import { MainMenu } from './MainMenu/MainMenu';
import * as styles from './MainNavSideBar.css';
import { MenuItem } from './MenuItem/MenuItem';
import * as menuItemStyles from './MenuItem/MenuItem.css';
import { UserMenu } from './UserMenu/UserMenu';

type MainNavSideBarProps = {
  toggleMenu: (value?: boolean) => void;
  isExpanded: boolean;
};

const OPEN_MENU_MOUSE_EVENT = 'mouseenter';
const CLOSE_MENU_MOUSE_EVENT = 'mouseleave';

export const MainNavSideBar = ({
  toggleMenu,
  isExpanded,
}: MainNavSideBarProps) => {
  const isMobile = useMatchMedia('mobile');
  const { loginContext, appContext, mainRoute } = useAppContext();

  const delayedOpen = useMemo(() => {
    return debounce(() => {
      toggleMenu(true);
    }, 300);
  }, [toggleMenu]);

  const toggleClose = useCallback(() => {
    delayedOpen.cancel();
    toggleMenu(false);
  }, [delayedOpen, toggleMenu]);

  const mainSideBarRef = useRef<HTMLDivElement>(null);

  // Used to avoid the effect to unsubscribe and subscribe again to DOM events
  // when using a callback that depends on this.
  const useRefExpanded = useRef<boolean>(isExpanded);
  useRefExpanded.current = isExpanded;

  // Handle click outside to close on mobile
  const handleClickOutside = useCallback(() => {
    if (isMobile && isExpanded) {
      toggleClose();
    }
  }, [isMobile, isExpanded, toggleClose]);

  // Only enable click-outside when on mobile and menu is expanded
  useOnClickOutside(
    isMobile && isExpanded ? mainSideBarRef : { current: null },
    handleClickOutside
  );

  const handleOnMouseEnter = useCallback(
    (e: MouseEvent) => {
      const isExpanded = useRefExpanded.current;
      if (!isExpanded) {
        delayedOpen();
      }
    },
    [delayedOpen]
  );

  const handleOnMouseLeave = useCallback(
    (e: MouseEvent) => {
      // Only close the menu if the target event is fired
      // from the main parent div
      if (mainSideBarRef.current === e?.target) {
        toggleClose();
      }
    },
    [toggleClose]
  );

  useEffect(() => {
    const sideBar = mainSideBarRef.current;
    sideBar?.addEventListener(OPEN_MENU_MOUSE_EVENT, handleOnMouseEnter);
    return () => {
      sideBar?.removeEventListener(OPEN_MENU_MOUSE_EVENT, handleOnMouseEnter);
    };
  }, [handleOnMouseEnter]);

  useEffect(() => {
    const sideBar = mainSideBarRef.current;
    sideBar?.addEventListener(CLOSE_MENU_MOUSE_EVENT, handleOnMouseLeave);
    return () => {
      sideBar?.removeEventListener(CLOSE_MENU_MOUSE_EVENT, handleOnMouseLeave);
    };
  }, [handleOnMouseLeave]);

  const hasHideNavAndFilterBarsFeature = useUserHasFeature(
    Feature.HideNavAndFilterBars
  );

  if (hasHideNavAndFilterBarsFeature && isInFullPageEventView(mainRoute)) {
    return null;
  }

  return (
    <div
      ref={mainSideBarRef}
      className={clsx(styles.root, {
        [styles.expanded]: isExpanded,
        [styles.mobileRoot]: isMobile,
      })}
    >
      <div className={styles.content}>
        {isMobile && (
          <div className={styles.closeButton} onClick={toggleClose}>
            <CrossIcon />
          </div>
        )}
        <div className={styles.topMenuArea}>
          <div className={styles.logoContainer}>
            <ProLogo className={styles.logo} />
          </div>
          <MainMenu toggleClose={toggleClose} />
          {hasAccessToRoute(
            loginContext?.user,
            appContext?.features,
            MainRoute.Email
          ) && <MessagesItem toggleClose={toggleClose} />}
          {hasAccessToRoute(
            loginContext?.user,
            appContext?.features,
            MainRoute.ReachProSupport
          ) && <ReachProSupportItem toggleClose={toggleClose} />}
          <CustomTopLevelTabs toggleClose={toggleClose} />
        </div>
        <hr className={styles.divider} />
        <div className={styles.bottomMenuArea}>
          <UserMenu toggleClose={toggleClose} />
        </div>
      </div>
    </div>
  );
};

const MessagesItem = ({ toggleClose }: { toggleClose: () => void }) => {
  const { icon, label, path, view } = MenuItems[MainRoute.Email];

  const { mainRoute } = useAppContext();

  return (
    <MenuItem
      isActive={view === mainRoute}
      icon={icon}
      textContent={label}
      path={path}
      onClick={toggleClose}
    />
  );
};

const ReachProSupportItem = ({ toggleClose }: { toggleClose: () => void }) => {
  const { icon, label, path, view } = MenuItems[MainRoute.ReachProSupport];

  const { mainRoute } = useAppContext();

  return (
    <MenuItem
      isActive={view === mainRoute}
      icon={icon}
      textContent={label}
      path={path}
      onClick={toggleClose}
    />
  );
};

const CustomTopLevelTabs = ({
  toggleClose,
}: {
  toggleClose: () => void;
}) => {
  const hasCustomTabsFeature = useUserHasFeature(Feature.CustomTabs);
  const { tabs } = useCustomTabsContext();
  const navigate = useNavigate();
  const topLevelTabs = tabs.filter((t) => t.scope === 'TopLevel');

  if (!hasCustomTabsFeature || topLevelTabs.length === 0) return null;

  return (
    <>
      {topLevelTabs.map((tab) => {
        const handleClick = () => {
          toggleClose();
          if (tab.tabType === 'Link' && tab.url) {
            const url = /^https?:\/\//i.test(tab.url) ? tab.url : `https://${tab.url}`;
            if (/^javascript:/i.test(url)) return;
            if (tab.openInNewTab !== false) {
              window.open(url, '_blank', 'noopener,noreferrer');
            } else {
              window.location.href = url;
            }
          } else {
            navigate(`/custom-tab/${tab.id}`);
          }
        };

        return (
          <div
            key={tab.id}
            className={menuItemStyles.menuItemContainer}
            onClick={handleClick}
            title={tab.description || tab.label}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick();
            }}
          >
            <div className={menuItemStyles.menuItem}>
              <div className={menuItemStyles.icon}>
                <CustomTabIcon
                  iconKey={tab.icon}
                  fill={IconsFill.currentColor}
                />
              </div>
              <span className={menuItemStyles.menuText}>{tab.label}</span>
            </div>
          </div>
        );
      })}
    </>
  );
};
